import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import { ForecastGridRowData, ForecastTemplateColumns } from 'src/models/ForecastModels';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { isMonthWithinBudgetEditableWindow } from '../forecast-utils/column-generator/generators/MonthColumnGenerator';
import { convertMonthFormatToDisplay } from 'src/utils/date-time-utilities';
import { logger } from 'src/analytics/KatalLogger';

const EDITABLE_MONTHS_VALIDATION_FAILED = 'Editable months validation failed';
const EDITABLE_MONTHS_VALIDATION_SUCCESS = 'Editable months validation passed';
const EDITABLE_MONTHS_VALIDATION_DEFAULT = 'Validates that data outside the budget editable months window has not been modified';

/**
 * Validates that forecast month data respects the budget-editable-months window.
 *
 * Rules (R&O scenario, Budget Leaders / Owners only):
 * - New rows: months OUTSIDE the editable window must be null (no data allowed).
 * - Existing rows: months OUTSIDE the editable window must not differ from the original row.
 *
 * Finance Admin / Owner are unrestricted — this validator is skipped for them.
 * When no window is configured (null/blank), all months are editable — validator passes.
 */
export class BudgetEditableMonthsValidationService extends ValidationService {
  public async validateEditableMonths(
    forecastTemplateRelevantRowData: ForecastGridRowData[],
    forecastTemplateColumns: ForecastTemplateColumns,
    forecastTemplateCompleteData: ForecastGridRowData[],
    budgetEditableStartMonth: string | null | undefined,
    budgetEditableEndMonth: string | null | undefined,
    isFinanceAdminOrFinanceOwner: boolean
  ): Promise<ValidationStatusEntity> {
    // Finance Admin / Owner are unrestricted
    if (isFinanceAdminOrFinanceOwner) {
      return this.createSuccessStatus(EDITABLE_MONTHS_VALIDATION_SUCCESS, EDITABLE_MONTHS_VALIDATION_DEFAULT);
    }

    // No window configured → all months editable (backward compatible)
    if (!budgetEditableStartMonth || !budgetEditableEndMonth) {
      return this.createSuccessStatus(EDITABLE_MONTHS_VALIDATION_SUCCESS, EDITABLE_MONTHS_VALIDATION_DEFAULT);
    }

    const validationErrorDetails: ValidationErrorDetail[] = [];
    const { forecastMonthColumnsIds } = forecastTemplateColumns;

    // Identify which forecast months are outside the editable window
    const readOnlyMonths = forecastMonthColumnsIds.filter(
      (monthId) => !isMonthWithinBudgetEditableWindow(monthId, budgetEditableStartMonth, budgetEditableEndMonth)
    );

    if (readOnlyMonths.length === 0) {
      // Editable window covers all forecast months
      return this.createSuccessStatus(EDITABLE_MONTHS_VALIDATION_SUCCESS, EDITABLE_MONTHS_VALIDATION_DEFAULT);
    }

    // Build a lookup map of existing rows by line item ID
    const completeDataMap = new Map<string, ForecastGridRowData>(
      forecastTemplateCompleteData.map((row) => [`${row[ForecastGridFixedFields.XptLineItemId.value]}`, row])
    );

    logger.info(`Validating budget editable months for ${forecastTemplateRelevantRowData.length} rows. Read-only months: ${readOnlyMonths.length}`);

    forecastTemplateRelevantRowData.forEach((row, rowIndex) => {
      const lineItemId = row[ForecastGridFixedFields.XptLineItemId.value];
      const isNewRow = !lineItemId || row[ForecastGridFixedFields.IsNewFERow.value];

      if (isNewRow) {
        // Rule 1: New rows must have null/empty for read-only months
        readOnlyMonths.forEach((monthId) => {
          const value = row[monthId];
          if (value !== null && value !== undefined && value !== '') {
            validationErrorDetails.push({
              rowIndex: rowIndex + 1,
              message: `New row has data in read-only month '${convertMonthFormatToDisplay(monthId)}' (value: ${value}) — outside the budget editable window ${this.getLineItemIdMessagePart(row, ForecastGridFixedFields.XptLineItemId.value)}`
            });
          }
        });
      } else {
        // Rule 2: Existing rows — read-only months must match original
        const originalRow = completeDataMap.get(`${lineItemId}`);
        if (originalRow) {
          readOnlyMonths.forEach((monthId) => {
            const currentValue = row[monthId];
            const originalValue = originalRow[monthId];

            // Normalize: treat null, undefined, and '' as equivalent (no data)
            const normalize = (v: unknown): number | null => (v == null || v === '') ? null : Number(v);
            const currentNum = normalize(currentValue);
            const originalNum = normalize(originalValue);

            if (currentNum !== originalNum) {
              validationErrorDetails.push({
                rowIndex: rowIndex + 1,
                message: `Month '${convertMonthFormatToDisplay(monthId)}' was modified (${originalValue} → ${currentValue}) — outside the budget editable window ${this.getLineItemIdMessagePart(row, ForecastGridFixedFields.XptLineItemId.value)}`
              });
            }
          });
        }
      }
    });

    this.logValidationErrors('Budget Editable Months', validationErrorDetails);

    if (validationErrorDetails.length > 0) {
      return this.createErrorStatus(
        EDITABLE_MONTHS_VALIDATION_FAILED,
        EDITABLE_MONTHS_VALIDATION_DEFAULT,
        validationErrorDetails
      );
    }

    return this.createSuccessStatus(EDITABLE_MONTHS_VALIDATION_SUCCESS, EDITABLE_MONTHS_VALIDATION_DEFAULT);
  }
}

// Export singleton instance
export const budgetEditableMonthsValidationService = new BudgetEditableMonthsValidationService();
